import {
  ChevronDown,
  ChevronRight,
  Download,
  HandCoins,
  Receipt,
  Search,
  Wallet,
} from 'lucide-react';
import { Fragment, useState } from 'react';

import { BarChart } from '@/components/charts/bar-chart';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { exportarCsv } from '@/lib/export';
import {
  cn,
  formatCurrencyAdaptive,
  formatDateTimeValue,
  formatNumber,
  toNumber,
} from '@/lib/utils';

import type { DetalleAutoservicio, SolicitudAutoservicio } from '../autoservicio.types';
import {
  ESTADOS_DOCUMENTACION,
  etiquetaComprobante,
  etiquetaDocumento,
  mensajeAutoservicio,
  numeroDeComprobante,
  varianteDocumento,
} from '../autoservicio-format';
import { KpiAutoservicio } from './kpi-autoservicio';

interface PanelOperacionesProps {
  datos: DetalleAutoservicio | undefined;
  cargando: boolean;
  error: unknown;
}

/** ¿La fila usó un código de saldo? */
function conCodigo(solicitud: SolicitudAutoservicio): boolean {
  return Boolean(solicitud.codigoSaldoAplicado) || toNumber(solicitud.saldoAplicado) > 0;
}

/** ¿Se emitió comprobante? */
function conComprobante(solicitud: SolicitudAutoservicio): boolean {
  return solicitud.numeroComprobante !== null && solicitud.numeroComprobante !== undefined;
}

/**
 * Pantalla **Operaciones**: detalle fila por operación.
 *
 * Añade a la tabla del contrato los agregados que se pueden derivar de las
 * filas: KPIs del listado, cobrado por serie, comprobantes, uso de código de
 * saldo, búsqueda, filtro por estado, ficha desplegable y exportación CSV.
 */
export function PanelOperaciones({ datos, cargando, error }: PanelOperacionesProps) {
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [expandidos, setExpandidos] = useState<ReadonlySet<number>>(new Set());

  if (error) {
    return (
      <Alert variant="error" title="No se pudo cargar el detalle">
        {mensajeAutoservicio(error)}
      </Alert>
    );
  }

  const solicitudes = datos?.solicitudes ?? [];
  const limite = toNumber(datos?.limite);

  const consulta = busqueda.trim().toLowerCase();
  const filas = solicitudes.filter((solicitud) => {
    if (estado !== '' && solicitud.estado !== estado) return false;
    if (consulta === '') return true;

    const textos = [
      String(solicitud.id),
      String(solicitud.solicitudAbastecimientoId),
      String(solicitud.turnoId),
      solicitud.serie ?? '',
      solicitud.codigoSaldoAplicado ?? '',
      solicitud.numeroComprobante === null ? '' : String(solicitud.numeroComprobante),
      solicitud.idVenta === null ? '' : String(solicitud.idVenta),
    ];

    return textos.some((texto) => texto.toLowerCase().includes(consulta));
  });

  const cobrado = filas.reduce((suma, fila) => suma + toNumber(fila.montoCobrado), 0);
  const presupuestado = filas.reduce((suma, fila) => suma + toNumber(fila.montoPresupuestado), 0);
  const saldo = filas.reduce((suma, fila) => suma + toNumber(fila.saldoAplicado), 0);
  const conSaldo = filas.filter(conCodigo).length;
  const emitidos = filas.filter(conComprobante).length;
  const boletas = filas.filter((fila) => fila.tipoComprobante === 'BOLETA').length;
  const facturas = filas.filter((fila) => fila.tipoComprobante === 'FACTURA').length;
  const sinComprobante = filas.length - emitidos;

  // Cobrado por serie (top 8, para que las etiquetas del eje se lean).
  const porSerie = new Map<string, { cobrado: number; presupuestado: number; cantidad: number }>();

  for (const fila of filas) {
    const clave = fila.serie?.trim() || 'Sin serie';
    const acumulado = porSerie.get(clave) ?? { cobrado: 0, presupuestado: 0, cantidad: 0 };

    acumulado.cobrado += toNumber(fila.montoCobrado);
    acumulado.presupuestado += toNumber(fila.montoPresupuestado);
    acumulado.cantidad += 1;
    porSerie.set(clave, acumulado);
  }

  const series = [...porSerie.entries()]
    .sort((a, b) => b[1].cobrado - a[1].cobrado)
    .slice(0, 8);

  const alternar = (id: number) => {
    setExpandidos((anterior) => {
      const siguiente = new Set(anterior);

      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);

      return siguiente;
    });
  };

  const exportar = () => {
    exportarCsv(
      'autoservicio-operaciones',
      [
        'Operación',
        'Solicitud',
        'Turno',
        'Fecha',
        'Serie',
        'Estado',
        'Comprobante',
        'Número',
        'Venta',
        'Presupuestado',
        'Cobrado',
        'Saldo aplicado',
        'Código de saldo',
      ],
      filas.map((fila) => [
        fila.id,
        fila.solicitudAbastecimientoId,
        fila.turnoId,
        formatDateTimeValue(fila.createdAt),
        fila.serie ?? '',
        fila.estado,
        etiquetaComprobante(fila.tipoComprobante),
        fila.numeroComprobante,
        fila.idVenta,
        toNumber(fila.montoPresupuestado),
        toNumber(fila.montoCobrado),
        toNumber(fila.saldoAplicado),
        fila.codigoSaldoAplicado ?? '',
      ]),
    );
  };

  const enElTope = limite > 0 && solicitudes.length >= limite;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiAutoservicio
          label="Operaciones visibles"
          value={formatNumber(filas.length)}
          hint={`${formatNumber(solicitudes.length)} recibidas · límite ${formatNumber(limite)}`}
          icon={Receipt}
          tono="brand"
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Monto cobrado"
          value={formatCurrencyAdaptive(cobrado)}
          hint={`Presupuestado ${formatCurrencyAdaptive(presupuestado)}`}
          icon={Wallet}
          tono="accent"
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Saldo aplicado"
          value={formatCurrencyAdaptive(saldo)}
          hint={`${formatNumber(conSaldo)} operaciones con código`}
          icon={HandCoins}
          tono="amber"
          cargando={cargando && !datos}
        />
        <KpiAutoservicio
          label="Comprobantes"
          value={formatNumber(emitidos)}
          hint={`${formatNumber(boletas)} boletas · ${formatNumber(facturas)} facturas · ${formatNumber(
            sinComprobante,
          )} sin comprobante`}
          icon={Receipt}
          tono="emerald"
          cargando={cargando && !datos}
        />
      </div>

      {filas.length > 0 ? (
        <div className="grid items-stretch gap-4 xl:grid-cols-3">
          <SectionCard
            className="xl:col-span-2"
            title="Cobrado por serie"
            description="Las 8 series con mayor monto cobrado en el filtro actual"
          >
            <BarChart
              labels={series.map(([serie]) => serie)}
              series={[
                { label: 'Cobrado', data: series.map(([, valor]) => valor.cobrado) },
                { label: 'Presupuestado', data: series.map(([, valor]) => valor.presupuestado) },
              ]}
              format="currency"
              height={300}
            />
          </SectionCard>

          <SectionCard title="Comprobantes y saldo" description="Composición del filtro actual">
            <div className="space-y-4">
              {[
                { etiqueta: 'Boletas', valor: boletas, tono: 'bg-brand-500' },
                { etiqueta: 'Facturas', valor: facturas, tono: 'bg-accent-500' },
                { etiqueta: 'Sin comprobante', valor: sinComprobante, tono: 'bg-amber-500' },
              ].map((fila) => (
                <div key={fila.etiqueta}>
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-ink-700">{fila.etiqueta}</span>
                    <span className="font-mono font-semibold text-ink-900 tabular-nums">
                      {formatNumber(fila.valor)}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
                    <span
                      className={cn('block h-full rounded-full', fila.tono)}
                      style={{ width: `${filas.length > 0 ? (fila.valor / filas.length) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}

              <div className="space-y-1 border-t border-ink-100 pt-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-ink-500">Operaciones con código</span>
                  <span className="font-mono font-semibold text-ink-900 tabular-nums">
                    {formatNumber(conSaldo)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-ink-500">Saldo redimido</span>
                  <span className="font-mono font-semibold text-ink-900 tabular-nums">
                    {formatCurrencyAdaptive(saldo)}
                  </span>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>
      ) : null}

      <SectionCard
        title="Operaciones del período"
        description="Comprobante emitido, montos y código de saldo por operación"
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={exportar}
            disabled={filas.length === 0}
            leftIcon={<Download className="size-3.5" aria-hidden="true" />}
          >
            Exportar
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Input
              label="Buscar operación"
              placeholder="Serie, comprobante, código, venta…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
              leftIcon={<Search className="size-4" aria-hidden="true" />}
            />
          </div>

          <div className="w-52">
            <label htmlFor="filtro-estado-operacion" className="mb-1.5 block text-sm font-medium text-ink-700">
              Estado
            </label>
            <Select
              id="filtro-estado-operacion"
              value={estado}
              onChange={(evento) => setEstado(evento.target.value)}
              className="h-12"
            >
              <option value="">Todos los estados</option>
              {ESTADOS_DOCUMENTACION.map((opcion) => (
                <option key={opcion.clave} value={opcion.clave}>
                  {opcion.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <span className="pb-3 text-[11px] text-ink-400">
            {formatNumber(filas.length)} de {formatNumber(solicitudes.length)} operación(es)
          </span>
        </div>

        {cargando && !datos ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((fila) => (
              <Skeleton key={fila} className="h-11 w-full" />
            ))}
          </div>
        ) : solicitudes.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">
            No hay operaciones de autoservicio en el período seleccionado.
          </p>
        ) : filas.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">
            Ninguna operación coincide con la búsqueda o el estado elegido.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                    <th className="w-[22%] pb-2 pr-2">Operación</th>
                    <th className="w-[12%] pb-2 pr-2">Serie</th>
                    <th className="w-[16%] pb-2 pr-2">Estado</th>
                    <th className="w-[18%] pb-2 pr-2">Comprobante</th>
                    <th className="w-[18%] pb-2 pr-2 text-right">Montos</th>
                    <th className="w-[14%] pb-2 text-right">Código saldo</th>
                  </tr>
                </thead>

                <tbody>
                  {filas.map((solicitud) => {
                    const abierto = expandidos.has(solicitud.id);

                    return (
                      <Fragment key={solicitud.id}>
                        <tr className="border-b border-ink-50">
                          <td className="py-3 pr-2 align-top">
                            <div className="flex items-start gap-1">
                              <button
                                type="button"
                                onClick={() => alternar(solicitud.id)}
                                aria-expanded={abierto}
                                aria-label={`${abierto ? 'Ocultar' : 'Ver'} ficha de la operación ${solicitud.id}`}
                                className="mt-0.5 cursor-pointer rounded p-0.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                              >
                                {abierto ? (
                                  <ChevronDown className="size-4" aria-hidden="true" />
                                ) : (
                                  <ChevronRight className="size-4" aria-hidden="true" />
                                )}
                              </button>

                              <div className="min-w-0">
                                <p className="font-mono text-sm font-semibold text-ink-900 tabular-nums">
                                  #{solicitud.id}
                                </p>
                                <p className="mt-0.5 truncate text-[11px] text-ink-400">
                                  {formatDateTimeValue(solicitud.createdAt)}
                                </p>
                                <p className="truncate text-[11px] text-ink-400">
                                  Turno #{solicitud.turnoId}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="py-3 pr-2 align-top">
                            <span className="font-mono text-sm text-ink-800">
                              {solicitud.serie || '—'}
                            </span>
                          </td>

                          <td className="py-3 pr-2 align-top">
                            <Badge variant={varianteDocumento(solicitud.estado)}>
                              {etiquetaDocumento(solicitud.estado)}
                            </Badge>
                          </td>

                          <td className="py-3 pr-2 align-top">
                            <p className="truncate text-sm text-ink-800">
                              {numeroDeComprobante(solicitud.tipoComprobante, solicitud.numeroComprobante)}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-ink-400">
                              {solicitud.idVenta === null
                                ? 'Sin venta asociada'
                                : `Venta #${solicitud.idVenta}`}
                            </p>
                          </td>

                          <td className="py-3 pr-2 text-right align-top">
                            <p
                              className="truncate font-mono text-sm font-semibold text-ink-900 tabular-nums"
                              title={formatCurrencyAdaptive(solicitud.montoCobrado)}
                            >
                              {formatCurrencyAdaptive(solicitud.montoCobrado)}
                            </p>
                            <p
                              className="mt-0.5 truncate text-[11px] text-ink-400"
                              title={`Presupuestado ${formatCurrencyAdaptive(solicitud.montoPresupuestado)}`}
                            >
                              Presup. {formatCurrencyAdaptive(solicitud.montoPresupuestado)}
                            </p>
                          </td>

                          <td className="py-3 text-right align-top">
                            <p
                              className="truncate font-mono text-sm text-ink-800"
                              title={solicitud.codigoSaldoAplicado ?? ''}
                            >
                              {solicitud.codigoSaldoAplicado || '—'}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-ink-400">
                              {toNumber(solicitud.saldoAplicado) > 0
                                ? formatCurrencyAdaptive(solicitud.saldoAplicado)
                                : 'Sin saldo'}
                            </p>
                          </td>
                        </tr>

                        {abierto ? (
                          <tr className="border-b border-ink-50 bg-ink-50/50">
                            <td colSpan={6} className="px-3 py-3">
                              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px] sm:grid-cols-3 lg:grid-cols-4">
                                <div>
                                  <dt className="text-ink-400">Solicitud de abastecimiento</dt>
                                  <dd className="font-mono text-ink-800">
                                    {solicitud.solicitudAbastecimientoId}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Registrada</dt>
                                  <dd className="text-ink-800">
                                    {formatDateTimeValue(solicitud.createdAt)}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Turno</dt>
                                  <dd className="text-ink-800">#{solicitud.turnoId}</dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Estado</dt>
                                  <dd className="text-ink-800">{etiquetaDocumento(solicitud.estado)}</dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Serie</dt>
                                  <dd className="font-mono text-ink-800">{solicitud.serie || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Comprobante</dt>
                                  <dd className="text-ink-800">
                                    {numeroDeComprobante(
                                      solicitud.tipoComprobante,
                                      solicitud.numeroComprobante,
                                    )}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Venta</dt>
                                  <dd className="text-ink-800">
                                    {solicitud.idVenta === null ? 'Sin venta' : `#${solicitud.idVenta}`}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Código de saldo</dt>
                                  <dd className="font-mono text-ink-800">
                                    {solicitud.codigoSaldoAplicado || 'No usó código'}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Presupuestado</dt>
                                  <dd className="text-ink-800">
                                    {formatCurrencyAdaptive(solicitud.montoPresupuestado)}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Cobrado</dt>
                                  <dd className="text-ink-800">
                                    {formatCurrencyAdaptive(solicitud.montoCobrado)}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-ink-400">Saldo aplicado</dt>
                                  <dd className="text-ink-800">
                                    {formatCurrencyAdaptive(solicitud.saldoAplicado)}
                                  </dd>
                                </div>
                              </dl>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-[11px] text-ink-400">
              Mostrando {formatNumber(filas.length)} operación(es) · límite {formatNumber(limite)}
              {enElTope ? ' — llegaste al tope, aumenta el límite para ver más.' : '.'}
            </p>
          </>
        )}
      </SectionCard>
    </div>
  );
}
